const TreeNode = require('../TreeNode');
const UseAbility = require('../UseAbility');
const BattleCalc = require('../../BattleCalc');
const AStarSearch = require('../../ai/AStarSearch/AStarSearch');
const { logError } = require('../../logger');

const MoveDecision = function (actor, battleState) {
  const node = new TreeNode(actor, battleState);

  // branch indexes
  node.JUMP_OR_MOVE = 0;
  node.NO_JUMP_ABILITY = 1;

  node.allTiles_list = Object.values(battleState.allTiles);

  const leap = BattleCalc.getEquippedAbility(actor, 'leap');
  const teleport = BattleCalc.getEquippedAbility(actor, 'teleport');

  const moveAbility = new UseAbility({
    actor,
    abilityHandle: 'move',
    altNode: null,
  });

  const teleportAbility = teleport
    ? new UseAbility({
        actor,
        abilityHandle: 'teleport',
        altNode: moveAbility,
      })
    : null;

  const leapAbility = leap
    ? new UseAbility({
        actor,
        abilityHandle: 'leap',
        altNode: null,
      })
    : null;

  node.branches[node.JUMP_OR_MOVE] = leap
    ? leapAbility
    : teleport
      ? teleportAbility
      : moveAbility;

  node.branches[node.NO_JUMP_ABILITY] = new UseAbility({
    actor,
    abilityHandle: 'move',
    altNode: null,
  });

  node.query = (input) => {
    if (!input || !actor || actor.dead || !actor.inPlay) return;

    const actor_tile = battleState.getTileAtCoords(actor.x, actor.y);
    if (input.uid === actor.uid || input.uid === actor_tile.uid) return;

    // no jump abilities while scared
    const scared = BattleCalc.getCondition(actor, 'scared');

    // getTargetTile depends on a list of tiles within range
    let leap_range = 0;
    let teleport_range = 0;
    const blessedFactor = BattleCalc.getBlessedFactor(actor);
    if (leap) {
      if (!Array.isArray(leap.apCost) && typeof leap.apCost === 'object')
        leap.apCost = Object.values(leap.apCost);

      for (
        let i = leap.apCost.length - 1; // leap.apCost is an array of costs by distance
        i >= 0;
        --i
      )
        if (actor.ap >= leap.apCost[i] * blessedFactor) {
          leap_range = i + 1;
          break;
        }
    }
    if (teleport) {
      if (!Array.isArray(teleport.mpCost) && typeof teleport.mpCost === 'object')
        teleport.mpCost = Object.values(teleport.mpCost);

      for (
        let i = teleport.mpCost.length - 1; // teleport.mpCost is an array of costs by distance
        i >= 0;
        --i
      )
        if (actor.mp >= teleport.mpCost[i] * blessedFactor) {
          teleport_range = i + 1;
          break;
        }
    }
    const move_range = scared ? 1 : Math.max(leap_range, teleport_range, 1); // 1 for default move ability
    const tiles_within_range = node.allTiles_list.filter(
      (tile) => !tile.occupied && BattleCalc.boardDistance(actor, tile) <= move_range
    );
    if (tiles_within_range.length === 0) return null;

    // considers the range of any ability we are trying to use (aiming to get just within it)
    let candidate_tiles = [];
    if (actor.abilityDestination) {
      const ability = BattleCalc.getEquippedAbility(actor, actor.abilityDestination.abilityHandle);
      const ability_victim = battleState.allUnits[actor.abilityDestination.victimId];
      // if we're moving away from the input to use a bow attack on them, just try to get range.min away
      if (
        node.is_move_away &&
        ['arrow_shot', 'blunt_arrow', 'leg_shot', 'multi_shot', 'razor_shot'].includes(
          ability.handle
        ) &&
        ability_victim.uid === input.uid
      ) {
        for (let r = ability.range.min; r <= ability.range.max; ++r) {
          for (const tile of tiles_within_range) {
            if (BattleCalc.boardDistance(tile, input) === r) candidate_tiles.push(tile);
          }
          if (candidate_tiles.length) break;
        }
        // otherwise, try to get just within range.max
      } else {
        for (let r = ability.range.max; r > 0; --r) {
          for (const tile of tiles_within_range)
            if (BattleCalc.boardDistance(tile, input) === r) candidate_tiles.push(tile);
          if (candidate_tiles.length) break;
        }
      }
    }
    if (candidate_tiles.length === 0) candidate_tiles = tiles_within_range;

    let target_tile;
    const input_tile = battleState.getTileAtCoords(input.x, input.y);

    if (move_range === 1 && !node.is_move_away) {
      // get adjacent tile on A* path to input_tile
      let path = AStarSearch(actor_tile, input_tile, battleState);

      if (path.length > 1) {
        target_tile = path[1]; // 0 being where the actor already is
      } else {
        // no A* path to the target_tile. recursively look for another target_tile among those adjacent
        let tile;
        let adjacentTiles;
        target_tile = input_tile;
        let dist = BattleCalc.boardDistance(actor, target_tile);

        let attempts = 0;
        try {
          while ((!path || path.length === 0) && dist > 0 && ++attempts < 30) {
            adjacentTiles = battleState.getAdjacentTiles(target_tile);
            for (tile of adjacentTiles) {
              const tile_dist = BattleCalc.boardDistance(actor, tile);
              if (tile_dist < dist) {
                target_tile = tile;
                dist = tile_dist;
                path = AStarSearch(actor_tile, target_tile, battleState);
                break;
              }
            }
          }
        } catch (err) {
          logError(err, {
            module: 'MoveDecision',
            func: 'getBestAdjacentTile',
            actor,
            battleState,
          });
        }

        if (path.length > 1) {
          target_tile = path[1];
        }
      }
    }
    // node.move_range > 1
    // getTargetTile must be defined by the "child" class (MoveTowardsDecision/MoveAwayDecision)
    else target_tile = node.getTargetTile(input_tile, candidate_tiles);

    if (!target_tile) return null;

    if (scared) return node.branches[node.NO_JUMP_ABILITY].query(target_tile);
    else return node.branches[node.JUMP_OR_MOVE].query(target_tile);
  };

  const originalDispose = node.dispose;
  node.dispose = function() {
    node.allTiles_list = null;
    originalDispose.call(node);
  };

  return node;
};
module.exports = MoveDecision;
